<template>
    <!-- Templates can only have a single root element -->
    <loading-box v-if="!isReady"></loading-box>
    <div v-else>
        <div class="col-12 primary header">
            <div class="pull-left d-inline-block">
                <span class="title">
                    <resource-string name="MediaLibrary_Browser_Title"></resource-string>
                </span>
            </div>
            
            <div class="pull-right d-inline-block">
                <!-- Only one of these will ever be visible at the same time -->
                <template v-if="canDelete">
                    <span @click="deleteFile(currentFolder, selectedFile)" v-if="selectedFile"><i class="fas fa-trash control-btn"></i></span>
                    <span @click="deleteFolder(selectedFolder)" v-if="selectedFolder"><i class="fas fa-trash control-btn"></i></span>
                </template>
                
                
                <span @click="displaySearch = !displaySearch"><i class="fas fa-search control-btn"></i></span>
                <span @click="growl('Closing')"><i class="fas fa-times control-btn"></i></span>
            </div>
        </div>
        
        <div class="col-12 search-container my-3" v-if="displaySearch">
            <div class="row justify-content-center">
                <div class="col-8">
                    <input class="form-control" v-model="search" v-bind:placeholder="resource('MediaLibrary_Search_Placeholder')" />
                </div>
            </div>
        </div>

        <div class="col-12 body" v-if="hasFolder">
            <div class="row">
                <div class="col-12">
                    <media-library-breadcrumbs
                        v-bind:base-folder="currentFolder"
                        @folder-selected="currentFolder = $event"
                    >
                    </media-library-breadcrumbs>
                </div>
            </div>

            <div class="row media-library-content-listing">
                <div class="col-12">
                    <div class="media-item-container" v-if="(!search && canWrite)">
                        <media-library-new-folder
                                v-bind:parent-id="currentFolder.id"
                                v-bind:media-library-id="currentFolder.mediaLibraryId"
                                v-on:folder-created="folderCreated($event, currentFolder)"
                        ></media-library-new-folder>
                    </div>
                    
                    <div class="media-item-container" v-for="folder in displayedFolders" v-bind:key="folder.id">
                        <media-library-folder
                                v-bind:name="folder.name"
                                v-bind:id="folder.id"
                                v-on:folder-selected="selectedFolder = folder"
                                v-on:folder-navigated="currentFolder = folder"
                                v-on:folder-renamed="folder.name = $event"
                                v-bind:class="{ selected: folder === selectedFolder }"
                        ></media-library-folder>
                    </div>

                    <div class="media-item-container" v-for="file in displayedFiles" v-bind:key="file.id">
                        <media-library-file
                                v-bind:name="file.name"
                                v-bind:id="file.id"
                                v-on:file-selected="selectedFile = file"
                                v-on:file-picked="pickFile(selectedFile)"
                                v-on:file-renamed="file.name = $event"
                                v-bind:class="{ selected: file === selectedFile }"
                        ></media-library-file>
                    </div>
                </div>
<!--                <div class="col-3">-->
<!--                    &lt;!&ndash; Reserved for preview &ndash;&gt;-->
<!--                </div>-->
            </div>
        </div>

        <div class="col-12 primary footer">
            <div class="file-upload-container" v-if="canWrite">
                <input
                        type="file"
                        ref="newMediaFile"
                        v-on:change="createFile(currentFolder, $refs.newMediaFile.files[0])"
                />
            </div>
            
            <button class="btn btn-default pull-right" v-if="selectedFile" v-on:click="pickFile(selectedFile)">
                <resource-string name="MediaLibrary_InsertFile_Button"></resource-string>
            </button>
        </div>
    </div>
</template>

<script>
    import mediaApi from '../../../Api/media-library';

    import {
        error as errorNotification,
        fileCreatedNotification, 
        fileDeletedNotification,
        folderCreatedNotification,
        folderDeletedNotification
    } from "../Util/notifications";

    import getResource from '../../../Util/resources';
    import securityApi from "../../../Util/security";
    import growlApi from "../../../Util/growler";
    import promptApi from "../../../Util/prompt";

    import CreateFolderBreadcrumbsMixin from '../Mixins/create-folder-breadcrumbs';

    import MediaLibraryFolder from './MediaLibraryFolder.vue';
    import MediaLibraryFile from './MediaLibraryFile.vue';
    import MediaLibraryNewFolder from './MediaLibraryNewFolder.vue';
    import MediaLibraryBreadcrumbs from "./MediaLibraryBreadcrumbs.vue";
    import ResourceString from '../../../Components/Util/ResourceString.vue';
    import LoadingBox from '../../../Components/Util/LoadingBox.vue';
    
    //Base URL used for History API
    const _baseUrl = '/MediaLibrary/Index/';
    
    export default {
        name: 'media-library',
        mixins: [CreateFolderBreadcrumbsMixin],
        components: {
            //We need to register our components in order to be able to use them
            MediaLibraryFolder,
            MediaLibraryFile,
            MediaLibraryNewFolder,
            MediaLibraryBreadcrumbs,

            ResourceString,
            LoadingBox
        },
        created () {
            const vm = this;

            //We want to start our request ASAP
            const fileInfoProm = vm.mediaId ? mediaApi.getFileInfo(vm.mediaId) : null;
            
            //Attempt to parse from URL when URL starts with baseUrl
            if(!vm.startFolder && location.pathname.indexOf(_baseUrl) === 0){
                //Simply replacing the first occurence should do the trick
                vm.startFolder = decodeURIComponent(
                    location.pathname.replace(_baseUrl, '')
                );
            }
            
            // fetch the data when the view is created and the data is
            // already being observed
            mediaApi.getCurrentLibrary().then((rootFolder) => {
                //Always start by initializing our rootFolder
                vm.rootFolder = rootFolder;

                // Only on component creation attempt to navigate to the given folder in the URL
                let startFolder = vm.findSubFolder(rootFolder, vm.startFolder);
                startFolder = startFolder || rootFolder;
                
                vm.currentFolder = startFolder;

                //Instantly complete when we don't have a preselected folder
                if(!fileInfoProm){
                    vm.isReady = true;
                    return;
                }

                fileInfoProm.then(fileInfo => {
                    //Attempt to find the folder our current media item is located in
                    const currentFolder = vm.flattenedFolders.find(folder => folder.id === fileInfo.folderId);

                    //Select said folder when visible
                    if(currentFolder){
                        vm.currentFolder = currentFolder;
                    }

                    vm.isReady = true;
                }, () => {
                    //In this case; rejection should not prevent the rest of the UI from rendering
                    //The file might be deleted, the user might have lost access to the folder the file is located in, etc.
                    vm.isReady = true
                });
            }, errorNotification);
            
            //Certain controls only get unlocked when a certain permission has been granted
            mediaApi.validatePermission(securityApi.PermissionGrantTypes.Write)
                .then((canWrite) => vm.canWrite = canWrite);

            mediaApi.validatePermission(securityApi.PermissionGrantTypes.Delete)
                .then((canDelete) => vm.canDelete = canDelete);
        },
        methods: {
            createFile(folder, file){
                //Convert our parameters to an object format known by our API
                const createModel = {
                    folderId: folder.id,
                    file: file
                };
                
                mediaApi.createFile(createModel).then((fileModel) => {
                    folder.files.push(fileModel);

                    fileCreatedNotification();
                }, errorNotification);
            },
            deleteFile(folder, file) {
                promptApi.prompt(
                    getResource('MediaLibrary_DeleteFile_PromptTitle'),
                    getResource('MediaLibrary_DeleteFile_PromptBody'),
                    (ok) => {
                        if(!ok){
                            return;
                        }

                        mediaApi.deleteFile(file.id).then(() => {
                            //Only delete the file from the folder when it is 
                            //still contained within the collection
                            const fileIndex = folder.files.indexOf(file);
                            if(fileIndex !== -1){
                                folder.files.splice(fileIndex, 1)
                            }
                            
                            fileDeletedNotification();
                        }, errorNotification);
                    }
                );
            },
            deleteFolder(folder) {
                const vm = this;

                promptApi.prompt(
                    getResource('MediaLibrary_DeleteFolder_PromptTitle'),
                    getResource('MediaLibrary_DeleteFolder_PromptBody'),
                    (ok) => {
                        if(!ok){
                            return;
                        }
                            
                        mediaApi.deleteFolder(folder.id).then(() => {
                            const parent = folder.parent;
                            if(parent){
                                //If our currentfolder is selected; we'll need to navigate to the parent
                                if(folder === vm.currentFolder){
                                    vm.currentFolder = parent;
                                }

                                //Only delete the folder from its parent when it is 
                                //still contained within the collection
                                const folderIndex = parent.subFolders.indexOf(folder);
                                if(folderIndex !== -1){
                                    parent.subFolders.splice(folderIndex, 1);
                                }
                            }

                            folderDeletedNotification();
                        }, errorNotification);
                    }
                );
            },
            folderCreated(folder, parent){
                parent.subFolders.push(folder);
                folder.parent = parent;

                folderCreatedNotification();
            },
            createBreadcrumbsForFolder(folder){
                const breadCrumbs = [folder];
                let currentFolder = folder;

                //Take everything starting from the current node
                //everything up until the root node can be used for breadcrumbs
                while (!!currentFolder.parent){
                    currentFolder = currentFolder.parent;
                    breadCrumbs.unshift(currentFolder);
                }

                return breadCrumbs;
            },
            findSubFolder(folder, path) {
                //Ensure our parameters are valid
                if(!path || !folder){
                    return undefined;
                }

                //Split path by '/'
                const pathParts = path.split('/');
                let currentFolder = folder;

                //We can back out of the loop once we've encountered our target folder or encountered an unknown folder name
                while (currentFolder && pathParts.length) {
                    //Take the first path part
                    const currentPart = pathParts.shift();

                    //Find the first subfolder matching the name of our current path part
                    currentFolder = currentFolder.subFolders.find((folder) => {
                        return currentPart.localeCompare(folder.name, undefined, { sensitivity: 'base' }) === 0;
                    });
                } 
                
                return currentFolder;
            },
            pickFile(file){
                const vm = this;
                vm.$emit('file-picked', file.id);
            },
            growl(text){
                growlApi.information(text);
            },
            resource(name) {
                return getResource(name);
            },
            saveFolderToUrl(folder){
                const vm = this;
                
                //We don't want to overwrite the url for a non-media library page
                if(!vm.isMediaLibraryPage){
                    return;
                }

                //Take all breadcrumbs
                const breadCrumbs = this.createBreadcrumbsForFolder(folder)
                    .map((folder) => folder.name);

                //Remove the first breadcrumb; add our current folder name
                breadCrumbs.shift();

                //Make an actual path out of our breadcrumbs
                const location = _baseUrl + breadCrumbs.join('/');

                //Push our new location to history
                window.history.pushState(
                    undefined,
                    document.title,
                    location
                );    
            }
        },
        computed: {
            hasFolder() {
                return this.currentFolder !== null;
            },
            flattenedFolders(){
                const flattedFolders = [];

                const flattenBranch = (currentFolder) => {
                    //Start of by pushing ourselves
                    flattedFolders.push(currentFolder);

                    //Then recursively process add our subfolders
                    currentFolder.subFolders.forEach(flattenBranch);
                };
                
                flattenBranch(this.rootFolder);

                return flattedFolders;
            },
            displayedFolders(){
                let search = this.search;
                let folders = this.currentFolder.subFolders;
                
                //Searching only works for files
                if(search){
                    //Convert to lowercase for 'case-insensitive' comparison
                    search = search.toLowerCase();
                    
                    folders = this.flattenedFolders
                        .filter((folder) => folder && folder.name && folder.name.toLowerCase().indexOf(search) !== -1);
                }

                //Sort by name
                return folders.sort(
                    (f1, f2) => f1.name.localeCompare(f2.name, undefined, { sensitivity: 'base' })
                );
            },
            displayedFiles(){
                let search = this.search;
                let files = this.currentFolder.files || [];
                
                if(search){
                    //Convert to lowercase for 'case-insensitive' comparison
                    search = search.toLowerCase();

                    //Mash all files together in a single collection
                    //Filter out everything not contained within our search value
                    files = this.flattenedFolders.flatMap((folder) => folder.files)
                        .filter((file) => file && file.name && file.name.toLowerCase().indexOf(search) !== -1);
                }
                
                //Sort by name
                return files.sort(
                    (f1, f2) => f1.name.localeCompare(f2.name, undefined, { sensitivity: 'base' })
                );
            },
            isMediaLibraryPage(){
                return location.pathname.indexOf(_baseUrl) === 0;
            }
        },
        watch: {
            currentFolder(folder) {
                const vm = this;
                
                //Reset our selected file + search
                vm.selectedFolder = null;
                vm.selectedFile = null;
                vm.displaySearch = false;
                
                vm.saveFolderToUrl(folder);
            },
            displaySearch(val) {
                //Always clear our current search value after toggle
                if(!val){
                    this.search = null;
                }
            },
            selectedFile(val){
                if(val){
                    this.selectedFolder = null;
                }
            },
            selectedFolder(val){
                if(val){
                    this.selectedFile = null;
                }
            }
        },
        data() { 
            return {
                isReady: false,
                
                rootFolder: null,
                currentFolder: null,
                startFolder: null,
                
                displaySearch: false,
                search: null,

                selectedFile: null,
                selectedFolder: null,
                
                canDelete: false,
                canWrite: false
            }
        },
        props: {
            mediaId: String
        }
    };
</script>

<style scoped src="../Style/media-library.scss" lang="scss">
</style>
